import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Alert,
    Button,
    Fade
} from '@mui/material';
import { explorerApi } from '../../services/api';
import UserPageHeader from './Components/UserPageHeader.component';
import UserStatsOverview from './Components/UserStatsOverview.component';
import UserVolumeOverview from './Components/UserVolumeOverview.component';
import UserTabsContainer from './Components/UserTabsContainer.component';
import UserConnections from '../../Components/UserConnections.component';

const UserPage = () => {
    const { userId } = useParams();
    const { t } = useTranslation();
    const [userData, setUserData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Fetch user data
    const fetchUserData = useCallback(async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);
        try {
            const response = await explorerApi.getUserDetails(userId);
            setUserData(response);
            setTransactions(response.transactions || []);
            setHasMore(response.pagination?.page < response.pagination?.totalPages);
        } catch (err) {
            setError(t('explorer.user.failedToLoadUserData'));
            console.error('User data fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [userId, t]);

    // Load more transactions
    const loadMoreTransactions = async () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);
        try {
            const response = await explorerApi.getUserDetails(userId, page + 1);
            setTransactions(prev => [...prev, ...response.transactions]);
            setPage(prev => prev + 1);
            setHasMore(response.pagination?.page < response.pagination?.totalPages);
        } catch (err) {
            console.error('Load more error:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="error" action={
                    <Button onClick={fetchUserData} size="small">{t('explorer.common.retry')}</Button>
                }>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!loading && !userData) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="info">
                    {t('explorer.user.userNotFound')}
                </Alert>
            </Container>
        );
    }

    return (
        <Fade in={true} timeout={1000}>
            <Container maxWidth="xl" sx={{ py: 4 }}>
                {/* Header */}
                <UserPageHeader
                    userId={userId}
                    loading={loading}
                    onRefresh={fetchUserData}
                />

                {/* Stats Overview */}
                <UserStatsOverview userData={userData} loading={loading} />

                {/* Volume Overview */}
                <UserVolumeOverview userData={userData} loading={loading} />

                {/* User Connections */}
                <UserConnections userId={userId} />

                {/* Tabs for different views */}
                <UserTabsContainer
                    tabValue={tabValue}
                    onTabChange={handleTabChange}
                    userId={userId}
                    transactions={transactions}
                    loading={loading}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    onLoadMoreTransactions={loadMoreTransactions}
                />
            </Container>
        </Fade>
    );
};

export default UserPage;
